import type { ModelInfo, Selection } from './api';

// ---------------------------------------------------------------------------
// The picker's memory and arithmetic, away from the components (204).
//
// Two things are remembered per browser, the way the theme and the menu are
// (theme.ts, nav.ts): the favourites, and the last model chosen on each
// bench. Per browser, not per account, because a favourite is a property of
// the person at the keyboard; the workspace's own default lives on the
// server (Settings → Models) and is what a bench starts from when this
// browser has never chosen. Only this file names the keys.
//
// The price arithmetic is pure so a test can read it: what the button shows
// until the server's quote answers is the option's own credits, never a rate
// typed into a component.
// ---------------------------------------------------------------------------

const FAV_KEY = 'rookery.picker.fav';
const LAST_KEY = (bench: string) => `rookery.picker.${bench}`;

export function readFavs(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(FAV_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 200) : [];
  } catch { return []; }
}

export function toggleFav(id: string): string[] {
  const now = readFavs();
  const next = now.includes(id) ? now.filter((x) => x !== id) : [...now, id];
  try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch { /* private mode: the choice lives for the session */ }
  return next;
}

export function readLast(bench: string): string | null {
  try { return localStorage.getItem(LAST_KEY(bench)); } catch { return null; }
}

export function writeLast(bench: string, id: string): void {
  try { localStorage.setItem(LAST_KEY(bench), id); } catch { /* see readTheme */ }
}

/**
 * Which model a bench starts on: the browser's last choice while it is
 * still offered (its replacement when it was retired), else the workspace's
 * default, else the house default, else the first that can be pressed.
 */
export function chooseModel(models: ModelInfo[], last: string | null): ModelInfo | null {
  if (!models.length) return null;
  const byId = new Map(models.map((m) => [m.id, m]));
  if (last) {
    const m = byId.get(last);
    if (m && m.status === 'active') return m;
    if (m?.status === 'deprecated' && m.replacement && byId.get(m.replacement.id)) return byId.get(m.replacement.id)!;
    if (m) return m;
  }
  return models.find((m) => m.workspaceDefault && m.status === 'active')
    ?? models.find((m) => m.houseDefault && m.status === 'active')
    ?? models.find((m) => m.status === 'active')
    ?? null;
}

export type SortKey = 'name' | 'price' | 'new';

export function sortModels(models: ModelInfo[], by: SortKey): ModelInfo[] {
  const out = [...models];
  if (by === 'name') out.sort((a, b) => a.name.localeCompare(b.name));
  else if (by === 'price') out.sort((a, b) => (a.credits ?? Number.MAX_SAFE_INTEGER) - (b.credits ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name));
  else out.sort((a, b) => Number(b.isNew) - Number(a.isNew) || Number(b.featured) - Number(a.featured) || a.name.localeCompare(b.name));
  return out;
}

export interface Filter { q?: string; privacy?: 'private' | 'anonymized' | null; vendor?: string | null; favsOnly?: boolean; favs?: string[] }

export function filterModels(models: ModelInfo[], f: Filter): ModelInfo[] {
  const q = (f.q ?? '').trim().toLowerCase();
  return models.filter((m) =>
    (!q || `${m.name} ${m.vendorId} ${m.vendor} ${m.blurb ?? ''}`.toLowerCase().includes(q))
    && (!f.privacy || m.privacy === f.privacy)
    && (!f.vendor || m.vendor === f.vendor)
    && (!f.favsOnly || (f.favs ?? []).includes(m.id)));
}

/** The choices a model starts on: the option marked default on each list it offers. */
export function defaultSelection(m: ModelInfo | null): Selection {
  if (!m) return {};
  const o = m.options;
  const sel: Selection = {};
  const res = o.resolutions?.find((r) => r.default) ?? o.resolutions?.[0];
  if (res) sel.resolution = res.id;
  const q = o.qualities?.find((r) => r.default);
  if (q) sel.quality = q.id;
  const d = o.durations?.find((r) => r.default) ?? o.durations?.[0];
  if (d) sel.seconds = d.seconds;
  if (o.audio) sel.audio = m.defaultVariant.endsWith('+audio');
  const s = o.scale?.find((r) => r.default) ?? o.scale?.[0];
  if (s) sel.scale = s.x;
  return sel;
}

/**
 * What the press would cost by the price list alone: the stand-in on the
 * button until the server's quote answers, and the number when it cannot.
 */
export function priceOf(m: ModelInfo | null, sel: Selection, units = 1): number | null {
  if (!m) return null;
  const o = m.options;
  if (o.scale && sel.scale) return o.scale.find((s) => s.x === sel.scale)?.credits ?? m.credits;
  if (o.qualities && sel.quality) return o.qualities.find((q) => q.id === sel.quality)?.credits ?? m.credits;
  if (m.unit === 'second') {
    const perSecond = sel.audio === false
      ? (o.audio?.off ?? m.credits)
      : (o.resolutions?.find((r) => r.id === sel.resolution)?.credits ?? o.audio?.on ?? m.credits);
    if (perSecond === null) return null;
    return Math.ceil(perSecond * (sel.seconds ?? 1) * units);
  }
  if (o.resolutions && sel.resolution) return o.resolutions.find((r) => r.id === sel.resolution)?.credits ?? m.credits;
  return m.credits === null ? null : Math.ceil(m.credits * units);
}

/** The price on a row or a chip: "368 cr", "153 cr / s", "3 cr / 1k tokens". */
export function sayPrice(credits: number | null, unit: string): string {
  if (credits === null) return '—';
  const n = credits.toLocaleString();
  switch (unit) {
    case 'second': return `${n} cr / s`;
    case 'minute': return `${n} cr / min`;
    case 'k_tokens': return `${n} cr / 1k tokens`;
    case 'k_chars': return `${n} cr / 1k chars`;
    default: return `${n} cr`;
  }
}

/** The words for a selection, for the quote line: "one 2K picture", "an 8-second shot with sound". */
export function sayWhat(bench: string, m: ModelInfo | null, sel: Selection): string {
  switch (bench) {
    case 'image': return `one ${sel.resolution ? `${sel.resolution} ` : sel.quality ? `${sel.quality}-quality ` : ''}picture`;
    case 'video': return `an ${sel.seconds ?? 8}-second shot${sel.audio === false ? ', silent' : ' with sound'}${sel.resolution ? ` at ${sel.resolution}` : ''}`;
    case 'music': return `a ${sel.seconds ?? 30}-second bed`;
    case 'upscale': return `one upscale ×${sel.scale ?? 2}`;
    default: return m ? `one press on ${m.name}` : 'one press';
  }
}

// ── The info panel's words (205) ────────────────────────────────────────────
// What a model is, what each of its settings costs, and what it can do, laid
// out from the server's own fields. Pure, so a test can read each sentence.

export interface PriceRow { label: string; credits: number | null; unit: string; default?: boolean }

/** Every priced setting of a model, one row each, the default first. */
export function priceRows(m: ModelInfo): PriceRow[] {
  const o = m.options;
  const rows: PriceRow[] = [];
  for (const r of o.resolutions ?? []) rows.push({ label: r.label, credits: r.credits, unit: m.unit, default: r.default });
  for (const q of o.qualities ?? []) rows.push({ label: `${q.label} quality`, credits: q.credits, unit: m.unit, default: q.default });
  for (const s of o.scale ?? []) rows.push({ label: `${s.x}× larger`, credits: s.credits, unit: m.unit, default: s.default });
  if (o.audio && (o.audio.on !== null || o.audio.off !== null)) {
    if (o.audio.on !== null) rows.push({ label: 'With sound', credits: o.audio.on, unit: m.unit });
    if (o.audio.off !== null) rows.push({ label: 'Silent', credits: o.audio.off, unit: m.unit });
  }
  if (o.tokens) {
    rows.push({ label: 'Reading (input)', credits: o.tokens.input, unit: 'k_tokens', default: true });
    rows.push({ label: 'Writing (output)', credits: o.tokens.output, unit: 'k_tokens' });
    if (o.tokens.cachedInput !== null) rows.push({ label: 'Reading a cached prompt', credits: o.tokens.cachedInput, unit: 'k_tokens' });
  }
  if (!rows.length) rows.push({ label: m.defaultVariant === 'each' || m.defaultVariant === 'bed' ? 'Each' : m.defaultVariant, credits: m.credits, unit: m.unit, default: true });
  return rows.sort((a, b) => Number(Boolean(b.default)) - Number(Boolean(a.default)));
}

/** What the credits cost in money at the workspace's rate: cents a thousand → a currency string, tenths of a cent below a cent. */
export function sayDollars(credits: number | null, centsPer1000: number | null): string | null {
  if (credits === null || !centsPer1000) return null;
  const dollars = (credits * centsPer1000) / 1000 / 100;
  if (dollars >= 0.01) return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(dollars);
  return `${(dollars * 100).toFixed(1)}¢`;
}

/** What a shot or a bed costs whole: the per-second price times the length. */
export function wholePrice(credits: number | null, unit: string, seconds: number): number | null {
  if (credits === null) return null;
  return unit === 'second' ? credits * seconds : credits;
}

const join = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);

/** The model's abilities in plain words, from its constraints. Each sentence is one fact; none states a price. */
export function sayConstraints(bench: string, m: ModelInfo): string[] {
  const c = m.constraints;
  const out: string[] = [];
  if (bench === 'video') {
    if (c.durations?.length) out.push(`Shots of ${join(c.durations.map(String))} seconds.`);
    if (c.resolutions?.length) out.push(`Renders at ${join(c.resolutions)}.`);
    if (c.audio === false) out.push('Silent: no sound is generated.');
    else if (c.audioConfigurable) out.push('Sound can be on or off.');
    else if (c.audio === true) out.push('Always generates sound with the picture.');
    if (c.modelType === 'image-to-video') out.push('Starts from a picture you give it.');
    else if (c.modelType === 'text-to-video') out.push('Starts from words; a reference picture is turned into an opening frame first.');
  }
  if (bench === 'image' || bench === 'edit') {
    if (m.options.resolutions?.length) out.push(`Sizes: ${join(m.options.resolutions.map((r) => r.label))}.`);
    if (m.options.qualities?.length) out.push(`Quality settings: ${join(m.options.qualities.map((q) => q.label))}.`);
  }
  if (bench === 'music') {
    if (c.minSeconds !== undefined && c.maxSeconds !== undefined) out.push(`Beds from ${c.minSeconds} to ${c.maxSeconds} seconds.`);
    else if (c.maxSeconds !== undefined) out.push(`Beds up to ${c.maxSeconds} seconds.`);
  }
  if (bench === 'text' && m.cacheDiscount) out.push(`A prompt it has just read is ${m.cacheDiscount}% cheaper to read again.`);
  if (bench === 'upscale' && m.options.scale?.length) out.push(`Enlarges ${join(m.options.scale.map((s) => `${s.x}×`))}.`);
  if (c.aspectRatios?.length) out.push(`Shapes: ${c.aspectRatios.join(', ')}.`);
  if (c.promptChars) out.push(`Prompts up to ${c.promptChars.toLocaleString()} characters.`);
  return out;
}

/** The vendor's privacy word, explained once. */
export function sayPrivacy(p: ModelInfo['privacy']): { word: string; means: string } {
  switch (p) {
    case 'private': return { word: 'Private', means: 'Runs on the vendor\'s own machines. Nothing you send is kept or used to train anything.' };
    case 'anonymized': return { word: 'Anonymous', means: 'Passed to the model\'s maker without anything that says who you are. The prompt is seen; you are not.' };
    default: return { word: 'Not stated', means: 'The vendor has not said how it handles what you send.' };
  }
}

/** The badge that says who wrote the blurb. The vendor's words need no badge. */
export function sayBlurbBy(by: ModelInfo['blurbBy']): string | null {
  return by === 'house' ? 'Written by the house' : by === 'curator' ? 'Written by our curators' : null;
}

/** When a deprecated model stops working, in words. */
export function sayRetirement(m: ModelInfo): string | null {
  if (m.status !== 'deprecated') return null;
  const when = m.removesAt ? ` on ${new Date(m.removesAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}` : ' soon';
  return m.replacement ? `Going away${when}. Choosing it picks ${m.replacement.name} instead.` : `Going away${when}.`;
}
